import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'

import { items } from '../../shared/Items'
import { isBackpackSlot, isEquipSlot, isHotSlot } from '../../shared/inventories'
import { ChatWindow } from './ChatWindow'
import { ItemTooltip, TOOLTIP_DELAY } from './ItemTooltip'
import { cls } from '../utils'

export function InterfaceExtra({
  extra,
  name,
  world,
  localInv,
  recipes,
  resonance,
  onGoHotbar,
  onGoEquipment,
  onGoChat,
  onTakeAll,
  onCraft,
  onCloseChat,
}) {
  if (extra.type === 'chat') {
    return <ChatWindow world={world} x={extra.x} y={extra.y} w={extra.w} h={extra.h} onClose={onCloseChat} />
  }
  if (extra.type === 'goHotbar') {
    return (
      <div
        className='interface-go-hotbar pointer'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: ${extra.w}px;
          height: ${extra.h}px;
          pointer-events: auto;
        `}
        onClick={onGoHotbar}
      />
    )
  }
  if (extra.type === 'goEquipment') {
    return (
      <div
        className='interface-go-equipment pointer'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: ${extra.w}px;
          height: ${extra.h}px;
          pointer-events: auto;
        `}
        onClick={onGoEquipment}
      />
    )
  }
  if (extra.type === 'goChat') {
    return (
      <div
        className='interface-go-chat pointer'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: ${extra.w}px;
          height: ${extra.h}px;
          pointer-events: auto;
        `}
        onClick={onGoChat}
      />
    )
  }
  if (extra.type === 'takeAll') {
    return (
      <div
        className='interface-take-all pointer'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: ${extra.w}px;
          height: ${extra.h}px;
          pointer-events: auto;
        `}
        onClick={onTakeAll}
      />
    )
  }
  if (extra.type === 'craftArea') {
    const slots = localInv?.slots || []
    return (
      <div
        className='craft noscrollbar'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: ${extra.w}px;
          height: ${extra.h}px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          /* gap: 2px; */
          pointer-events: auto;
          .craft-recipe {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 1px;
            /* background: rgba(0, 0, 0, 0.2); */
          }
          .craft-info {
            flex: 1;
          }
          .craft-name {
            font-size: 6px;
            line-height: 1;
            /* margin: 0 0 1px; */
          }
          .craft-inputs {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
          }
          .craft-input {
            font-size: 4px;
            color: #ff4545;
            &.have {
              color: #2ae12a;
            }
          }
          .craft-btn {
            height: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 3px;
            flex-shrink: 0;
            background: #273038;
            color: #384550;
            border-radius: 1px; // todo: we should use pixel art bg
            border-bottom: 1px solid rgba(0, 0, 0, 0.2);
            > span {
              font-size: 6px;
              line-height: 0;
              pointer-events: none;
            }
            &.active {
              background: #248124;
              color: #fff;
            }
          }
        `}
      >
        {(recipes || []).map(recipe => {
          const ingMeta = recipe.input.map(ing => {
            const have = slots.reduce((sum, s, i) => (isBackpackSlot(i) && s?.id === ing.id ? sum + s.qty : sum), 0)
            return { ing, have, ok: have >= ing.qty }
          })
          const canCraft = ingMeta.every(m => m.ok)
          let freedSlots = 0
          for (const ing of recipe.input) {
            let toConsume = ing.qty
            for (let i = 0; i < slots.length && toConsume > 0; i++) {
              if (!isBackpackSlot(i)) continue
              const s = slots[i]
              if (s?.id !== ing.id) continue
              const taking = Math.min(toConsume, s.qty)
              toConsume -= taking
              if (taking === s.qty) freedSlots++
            }
          }
          const outputItem = items[recipe.output.id]
          const max = outputItem?.max ?? 1
          let roomRemaining = recipe.output.qty
          // Count space in existing partial stacks in backpack and hotbar (not gear)
          if (max > 1) {
            for (let i = 0; i < slots.length && roomRemaining > 0; i++) {
              if (isEquipSlot(i)) continue
              if (isHotSlot(i) && !outputItem?.consumable) continue
              const s = slots[i]
              if (s?.id === recipe.output.id && s.qty < max) roomRemaining -= max - s.qty
            }
          }
          const emptyBackpack = slots.filter((s, i) => isBackpackSlot(i) && !s).length + freedSlots
          const emptyHotbar = outputItem?.consumable ? slots.filter((s, i) => isHotSlot(i) && !s).length : 0
          const hasRoom = roomRemaining <= 0 || emptyBackpack + emptyHotbar >= Math.ceil(roomRemaining / max)
          return (
            <div className='craft-recipe' key={recipe.id}>
              <RecipeIcon item={outputItem} />
              <div className='craft-info'>
                <div className='craft-name'>{outputItem?.name || recipe.output.id}</div>
                <div className='craft-inputs'>
                  {ingMeta.map((m, i) => (
                    <span key={m.ing.id} className={cls('craft-input', { have: m.ok })}>
                      {i > 0 ? ', ' : ''}
                      {items[m.ing.id]?.name || m.ing.id} x{m.ing.qty}
                    </span>
                  ))}
                </div>
              </div>
              <div
                className={cls('craft-btn', { pointer: canCraft && hasRoom, active: canCraft && hasRoom })}
                onClick={canCraft && hasRoom ? () => onCraft(recipe.id) : undefined}
              >
                <span>Craft</span>
              </div>
            </div>
          )
        })}
      </div>
    )
  }
  if (extra.type === 'resonance') {
    const pct = Math.max(0, Math.min(100, resonance ?? 100))
    if (pct >= 100) return null
    return (
      <div
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          width: 82px;
          height: 4px;
          background-image: url('/assets/resonance-bar.png');
          overflow: hidden;
          padding: 1px;
          .resonance-bar {
            height: 100%;
            width: 100%;
            background: linear-gradient(to bottom, #5f1ad0 50%, #5213b9 50%);
            transform-origin: left;
            transition: transform 0.5s ease-out;
          }
        `}
      >
        <div className='resonance-bar' style={{ transform: `scaleX(${pct / 100})` }} />
      </div>
    )
  }
  if (extra.type === 'text') {
    let text = extra.text
    text = text.replace('{name}', name)
    return (
      <div
        className='interface-text'
        css={css`
          position: absolute;
          top: ${extra.y}px;
          left: ${extra.x}px;
          font-size: ${extra.size}px;
          line-height: 1;
        `}
      >
        {text}
      </div>
    )
  }
  return null
}

function RecipeIcon({ item }) {
  const ref = useRef(null)
  const [cellRect, setCellRect] = useState(null)
  const genRef = useRef(0)
  const timerRef = useRef(null)

  const handlePointerEnter = e => {
    const gen = ++genRef.current
    const delay = e.pointerType === 'touch' ? TOOLTIP_DELAY : 0
    timerRef.current = setTimeout(() => {
      if (gen === genRef.current && ref.current) setCellRect(ref.current.getBoundingClientRect())
    }, delay)
  }

  const handlePointerLeave = () => {
    genRef.current++
    clearTimeout(timerRef.current)
    setCellRect(null)
  }

  return (
    <div
      ref={ref}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      css={css`
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        background: url(${item?.icon}) center/cover no-repeat;
        image-rendering: pixelated;
        position: relative;
      `}
    >
      {cellRect && item && <ItemTooltip item={item} cellRect={cellRect} />}
    </div>
  )
}
